import React, { useCallback } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactFileUpload from 'shared/design-system/components/TrueImpactFileUpload';
import { downloadSampleCSVFileUrl } from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FileUploadResponse } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';

const MAX_FILE_SIZE = 512 * 1000 * 1000; // 512mb
const acceptedFileString = 'CSV';
const allowedFileExtensions = ['text/csv', 'application/vnd.ms-excel'];

interface ContributionsFileUploadProps {
  file: File | undefined;
  moveToMappingStep: () => unknown;
  setFile: React.Dispatch<React.SetStateAction<File | undefined>>;
}

const StyledDiv = styled.div`
  text-align: center;
  max-width: 669px;
  margin-left: auto;
  margin-right: auto;
  .file-upload-heading {
    margin-bottom: ${SPACERS.medium};
  }
  .file-upload-subheading {
    margin-bottom: ${SPACERS.medium};
  }
  .file-sample-download {
    margin-bottom: ${SPACERS.tiny};
    a {
      text-decoration: none;
      color: ${COLORS.ctaPrimary};
    }
  }
  .file-sample-download-note {
    margin-bottom: ${SPACERS.extraLarge};
    strong {
      font-weight: normal;
      padding: 2px ${SPACERS.tiny};
      border-radius: ${SPACERS.extraTiny};
      background-color: ${COLORS.grayLight};
    }
  }
  .field-mapping-button {
    margin-bottom: ${SPACERS.medium};
    button {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

const ContributionsFileUpload: React.FC<ContributionsFileUploadProps> = ({
  file,
  setFile,
  moveToMappingStep,
}) => {
  const handleFileUpload = useCallback(
    async (file: File | undefined): Promise<FileUploadResponse> => {
      return new Promise((resolve, reject) => {
        if (!file) return reject();
        setFile(file);
        return resolve({
          success: true,
          id: 0,
          url: '',
        });
      });
    },
    [setFile],
  );

  return (
    <StyledDiv>
      <TrueImpactText variant="heading-page" className="file-upload-heading">
        Please upload your bulk donations file.
      </TrueImpactText>
      <TrueImpactText className="file-upload-subheading">
        After you upload, you’ll be able to match your data to the correct
        headers before confirming your import.
      </TrueImpactText>
      <TrueImpactText className="file-sample-download">
        Need a CSV template to get you started?{' '}
        <a
          href={downloadSampleCSVFileUrl}
          download="Impact Receipt Investment Upload Template.csv"
        >
          Download our starter template here
        </a>
        .
      </TrueImpactText>
      <TrueImpactText className="file-sample-download-note">
        Note: <strong>Tags</strong> and <strong>External Record ID</strong> are
        optional columns.
      </TrueImpactText>
      {file && (
        <div className="field-mapping-button">
          <TrueImpactButton variant="primary-large" onClick={moveToMappingStep}>
            Go to field mapping (Unsaved changes)
          </TrueImpactButton>
        </div>
      )}
      <TrueImpactFileUpload
        title="Drag your file here or click to upload"
        initialFile={undefined}
        acceptedFileExtensions={allowedFileExtensions}
        acceptedFiles={acceptedFileString}
        handleFileUpload={handleFileUpload}
        maxFileSize={MAX_FILE_SIZE}
      />
    </StyledDiv>
  );
};

export default ContributionsFileUpload;
